import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerFaceAssessmentSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { apiErrorMessage } from '@/services/apiClient';
import { useVoidFaceAssessmentMutation } from '../api/face.api';

/** The shortest reason the API will accept, so the field can say so before the request is made. */
const MIN_REASON = 3;

/**
 * Marking an assessment as mistaken.
 *
 * A dialog rather than a browser prompt: a prompt cannot be translated, cannot be styled, cannot say
 * what it will do, and cannot be reached the same way by a keyboard on every platform. What is being
 * voided is named, so a slip on the wrong row is visible before it is confirmed.
 */
export function VoidFaceAssessmentDialog({
  assessment,
  isOpen,
  onClose,
}: {
  assessment: CustomerFaceAssessmentSummary | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const voidAssessment = useVoidFaceAssessmentMutation();

  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setError(null);
  }, [isOpen]);

  const trimmed = reason.trim();
  const ready = trimmed.length >= MIN_REASON;

  const submit = () => {
    if (!assessment || !ready) return;
    setError(null);
    voidAssessment.mutate(
      { id: assessment.id, reason: trimmed },
      {
        onSuccess: onClose,
        onError: (caught) => setError(apiErrorMessage(caught) ?? t('face.errors.void')),
      },
    );
  };

  const shape = assessment?.confirmedShape;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('face.voidDialog.title')}
      description={t('face.voidDialog.description')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            disabled={!ready}
            isLoading={voidAssessment.isPending}
          >
            {t('face.voidDialog.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {shape ? (
          <p className="text-ink text-sm">
            {t('face.voidDialog.voiding', { shape: t(`face.shapes.${shape}`) })}
          </p>
        ) : null}

        <div className="space-y-1.5">
          <label htmlFor="void-reason" className="text-ink block text-sm font-medium">
            {t('face.voidDialog.reasonLabel')}
          </label>
          <Input
            id="void-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('face.voidDialog.reasonPlaceholder')}
            maxLength={500}
            autoComplete="off"
            aria-describedby="void-reason-hint"
          />
          <p id="void-reason-hint" className="text-muted text-xs">
            {t('face.voidDialog.reasonHint')}
          </p>
        </div>
      </div>
    </Modal>
  );
}
